import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import BoardLoading from "@/components/BoardLoading";
import BoardWorkspace from "@/components/BoardWorkspace";
import { TypedDateInput } from "@/components/TypedDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const money = (value) => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(Number(value || 0));

export default function ProductionTrackerPage() {
  const { canAccessBilling } = useAuth();
  const [tracker, setTracker] = useState(null);
  const [error, setError] = useState("");
  const [manualWeek, setManualWeek] = useState({ week_start: "", invoices_sent: "", invoices_paid: "", note: "" });

  const load = useCallback(async () => {
    try {
      setError("");
      const response = await axios.get(`${API}/production-tracker`);
      setTracker(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Production Tracker could not be loaded.");
    }
  }, []);

  useEffect(() => {
    if (canAccessBilling) load();
  }, [canAccessBilling, load]);

  const saveManualWeek = async (event) => {
    event.preventDefault();
    const payload = { week_start: manualWeek.week_start, week_end: sundayFor(manualWeek.week_start), invoices_sent: Number(manualWeek.invoices_sent || 0), invoices_paid: Number(manualWeek.invoices_paid || 0), source_note: manualWeek.note };
    try {
      const existing = tracker.rows.find((row) => row.week_start === manualWeek.week_start);
      if (existing) await axios.patch(`${API}/production-tracker/weeks/${existing.id}`, payload);
      else await axios.post(`${API}/production-tracker/weeks`, payload);
      toast.success("Past-week totals saved. App invoice records replace these amounts when available.");
      setManualWeek({ week_start: "", invoices_sent: "", invoices_paid: "", note: "" });
      await load();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Past-week totals could not be saved.");
    }
  };

  if (!canAccessBilling) return <Navigate replace to="/master-project-board" />;
  if (!tracker && !error) return <BoardLoading label="Loading Production Tracker" />;
  return (
    <BoardWorkspace
      eyebrow="JFM company operations"
      headingTestId="production-page-title"
      title="Production Tracker"
      subtitle="Whole-company weekly operating summary from the approved tracker sheet."
      actions={(
        <button
          className="board-refresh-button"
          data-testid="production-refresh-button"
          onClick={load}
          type="button"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      )}
    >
      {error ? (
        <section className="board-error-state" data-testid="production-error">
          <p data-testid="production-error-message">{error}</p>
          <button data-testid="production-retry-button" onClick={load} type="button">
            Try again
          </button>
        </section>
      ) : (
        <>
          <section className="production-summary" data-testid="production-summary">
            <Metric
              label="Tracked weeks"
              testId="production-tracked-weeks"
              value={tracker.summary.tracked_weeks}
            />
            <Metric
              label="Total invoiced / sent"
              testId="production-sent-total"
              value={money(tracker.summary.invoices_sent_total)}
            />
            <Metric
              label="Total invoiced / paid"
              testId="production-paid-total"
              value={money(tracker.summary.invoices_paid_total)}
            />
          </section>
          <section className="invoice-table-panel" data-testid="production-table-panel">
            <div className="board-table-caption">
              <div>
                <span className="operations-kicker">Approved weekly record</span>
                <h2>Company weekly summary</h2>
              </div>
              <p>Paid totals update from recorded invoice payment dates.</p>
            </div>
            <div className="board-table-scroll">
              <table className="operations-data-table production-table" data-testid="production-table">
                <thead>
                  <tr>
                    <th><span>Week of</span></th>
                    <th><span>Amount invoiced / sent</span></th>
                    <th><span>Amount invoiced / paid</span></th>
                  </tr>
                </thead>
                <tbody>
                  {tracker.rows.map((row) => (
                    <tr data-testid={`production-row-${row.id}`} key={row.id}>
                      <td data-testid={`production-week-${row.id}`}>
                        {formatWeek(row.week_start, row.week_end)}
                      </td>
                      <td data-testid={`production-invoices-sent-${row.id}`}>
                        {displayMoney(row.invoices_sent)}
                      </td>
                      <td data-testid={`production-invoices-paid-${row.id}`}>
                        {displayMoney(row.invoices_paid)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!tracker.rows.length && (
                <div className="board-empty-state" data-testid="production-empty-state">
                  No weekly production summaries have been recorded.
                </div>
              )}
            </div>
          </section>
          <section className="invoice-table-panel" data-testid="production-paid-invoices-panel">
            <div className="board-table-caption"><div><span className="operations-kicker">Payment record</span><h2>Invoices paid</h2></div></div>
            <div className="board-table-scroll"><table className="operations-data-table" data-testid="production-paid-invoices-table"><thead><tr><th>Invoice</th><th>Project</th><th>Customer</th><th>Paid date</th><th>Paid amount</th></tr></thead><tbody>{tracker.paid_invoices.map((invoice) => <tr data-testid={`production-paid-invoice-${invoice.id}`} key={invoice.id}><td>{invoice.invoice_number || "—"}</td><td>{invoice.project_number || "—"}</td><td>{invoice.customer || "—"}</td><td>{invoice.payment_date || "—"}</td><td>{money(invoice.paid_amount)}</td></tr>)}</tbody></table>{!tracker.paid_invoices.length && <div className="board-empty-state" data-testid="production-paid-invoices-empty">No paid invoices have been recorded.</div>}</div>
          </section>
          <form className="production-manual-week-form" data-testid="production-manual-week-form" onSubmit={saveManualWeek}>
            <span className="operations-kicker">Past-week entry</span><h2>Record a week missing from the app</h2>
            <label>Week beginning (Monday)<TypedDateInput data-testid="production-manual-week-start" onChange={(value) => setManualWeek({ ...manualWeek, week_start: value })} required value={manualWeek.week_start} /></label>
            <label>Amount invoiced / sent<input data-testid="production-manual-sent" min="0" onChange={(event) => setManualWeek({ ...manualWeek, invoices_sent: event.target.value })} step="0.01" type="number" value={manualWeek.invoices_sent} /></label>
            <label>Amount invoiced / paid<input data-testid="production-manual-paid" min="0" onChange={(event) => setManualWeek({ ...manualWeek, invoices_paid: event.target.value })} step="0.01" type="number" value={manualWeek.invoices_paid} /></label>
            <label>Note<textarea data-testid="production-manual-note" onChange={(event) => setManualWeek({ ...manualWeek, note: event.target.value })} value={manualWeek.note} /></label>
            <button data-testid="production-manual-save" type="submit">Save past week</button>
          </form>
        </>
      )}
    </BoardWorkspace>
  );
}

function Metric({ label, testId, value }) {
  return (
    <article data-testid={testId}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function displayMoney(value) {
  return value === null || value === undefined ? "Pending" : money(value);
}

function formatWeek(start, end) {
  const format = (value) => {
    const [year, month, day] = value.split("-");
    return `${Number(month)}/${Number(day)}/${year.slice(-2)}`;
  };
  return `${format(start)}–${format(end)}`;
}

function sundayFor(weekStart) {
  const monday = new Date(`${weekStart}T12:00:00`);
  monday.setDate(monday.getDate() + 6);
  return monday.toISOString().slice(0, 10);
}